import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  BarChart3,
  Brain,
  FileText,
  TrendingUp,
  Users,
  MousePointer,
  DollarSign,
  Eye,
  Globe,
  Hash,
  Link,
  Sparkles,
  BookOpen,
  Tag,
  Smile
} from 'lucide-react';
import { Card, Button, Drawer } from '@/components/ui';
import { NewsletterPost, NewsletterBenchmarks, NewsletterInsights, NewsletterPostClicks } from '@/types/newsletter';
import { useNewsletterStore } from '@/stores/newsletterStore';
import { newsletterService } from '@/services/newsletterService';

interface PostDetailDrawerProps {
  post: NewsletterPost | null;
  isOpen: boolean;
  onClose: () => void;
  posts?: NewsletterPost[];
  onNavigate?: (post: NewsletterPost) => void;
}

type TabType = 'stats' | 'clicks' | 'insights' | 'content' | 'analysis';

interface ContentAnalysis {
  wordCount: number;
  subjectLineLength: number;
  hasEmoji: boolean;
  sentimentScore: number;
  readabilityScore: number;
  primaryTopics: string[];
  keywords: string[];
  entities: string[];
  analysisDate: string;
}

const PostDetailDrawer: React.FC<PostDetailDrawerProps> = ({
  post,
  isOpen,
  onClose,
  posts = [],
  onNavigate
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('stats');
  const {
    isLoading,
    error
  } = useNewsletterStore();

  if (!post) return null;

  // Use pre-loaded data from the post object
  const benchmarks = post.benchmarks || null;
  const insights = post.insights || null;
  const clicks = post.clicks || null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPercentage = (value: number) => `${value.toFixed(1)}%`;
  const formatCurrency = (value: number) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatNumber = (value: number) => value.toLocaleString();

  const currentIndex = posts.findIndex(p => p.id === post.id);
  const canNavigatePrev = currentIndex > 0;
  const canNavigateNext = currentIndex < posts.length - 1;

  const handleNavigate = (direction: 'prev' | 'next') => {
    if (!onNavigate) return;

    const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex >= 0 && newIndex < posts.length) {
      onNavigate(posts[newIndex]);
    }
  };

  const tabs = [
    { id: 'stats', label: 'Performance Stats', icon: BarChart3 },
    { id: 'clicks', label: 'Click Analysis', icon: MousePointer },
    { id: 'insights', label: 'AI Insights', icon: Brain },
    { id: 'analysis', label: 'Analysis', icon: Sparkles },
    { id: 'content', label: 'Content Analysis', icon: FileText }
  ];

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={post.title}
      description={`${formatDate(post.publishDate)} • ${formatNumber(post.stats.totalSent)} recipients`}
      isCustomWidth={true}
      customWidth="max-w-md"
    >
      <div className="space-y-4">
        {/* Navigation and Action Buttons */}
        <div className="flex items-center justify-between">
          {posts.length > 1 && (
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={!canNavigatePrev}
                onClick={() => handleNavigate('prev')}
                icon={<ArrowLeft className="h-4 w-4" />}
              />
              <span className="text-sm text-gray-500 px-2">
                {currentIndex + 1} of {posts.length}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={!canNavigateNext}
                onClick={() => handleNavigate('next')}
                icon={<ArrowRight className="h-4 w-4" />}
              />
            </div>
          )}

          {post.webUrl && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(post.webUrl, '_blank')}
              icon={<ExternalLink className="h-4 w-4" />}
            >
              View Post
            </Button>
          )}
        </div>

        {/* Key Metrics Summary */}
        <div className="grid grid-cols-2 gap-3">
          <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-2xl font-bold text-purple-900">
              {formatPercentage(post.stats.averageOpenRate)}
            </p>
            <p className="text-xs text-purple-700 font-medium">Open Rate</p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-2xl font-bold text-blue-900">
              {formatPercentage(post.stats.averageClickRate)}
            </p>
            <p className="text-xs text-blue-700 font-medium">Click Rate</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="text-2xl font-bold text-green-900">
              {formatCurrency(post.stats.emv)}
            </p>
            <p className="text-xs text-green-700 font-medium">EMV</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-2xl font-bold text-gray-900">
              {formatNumber(post.stats.totalUniqueOpened)}
            </p>
            <p className="text-xs text-gray-700 font-medium">Unique Opens</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-4">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id as TabType)}
                className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-xs ${
                  activeTab === id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center">
                <div className="text-red-600 mr-2">⚠️</div>
                <div>
                  <p className="text-sm text-red-800 font-medium">Error loading data</p>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'stats' && (
            <StatsTab post={post} benchmarks={benchmarks} />
          )}
          {activeTab === 'clicks' && (
            <ClickAnalysisTab clicks={clicks} />
          )}
          {activeTab === 'insights' && (
            <InsightsTab insights={insights} isLoading={isLoading} />
          )}
          {activeTab === 'analysis' && (
            <AnalysisTab postId={post.id} />
          )}
          {activeTab === 'content' && (
            <ContentTab post={post} />
          )}
        </div>
      </div>
    </Drawer>
  );
};

// Stats Tab Component
interface StatsTabProps {
  post: NewsletterPost;
  benchmarks: NewsletterBenchmarks | null;
}

const StatsTab: React.FC<StatsTabProps> = ({ post, benchmarks }) => {
  const deliveryRate = post.stats.totalSent > 0
    ? (post.stats.totalDelivered / post.stats.totalSent) * 100
    : 0;

  const metrics = [
    {
      label: 'Total Sent',
      value: post.stats.totalSent.toLocaleString(),
      icon: Users,
      color: 'blue'
    },
    {
      label: 'Delivered',
      value: `${post.stats.totalDelivered.toLocaleString()} (${deliveryRate.toFixed(1)}%)`,
      icon: TrendingUp,
      color: 'green'
    },
    {
      label: 'Unique Opens',
      value: post.stats.totalUniqueOpened.toLocaleString(),
      icon: Eye,
      color: 'purple'
    },
    {
      label: 'Total Clicks',
      value: post.stats.totalClicked.toLocaleString(),
      icon: MousePointer,
      color: 'orange'
    },
    {
      label: 'EMV',
      value: `$${post.stats.emv.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: DollarSign,
      color: 'green'
    }
  ];

  return (
    <div className="space-y-4">
      {/* Detailed Metrics */}
      <div className="grid grid-cols-1 gap-3">
        {metrics.map((metric, index) => (
          <Card key={index} variant="outlined" padding="md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600">{metric.label}</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{metric.value}</p>
              </div>
              <div className={`p-2 rounded-lg ${
                metric.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                metric.color === 'green' ? 'bg-green-100 text-green-600' :
                metric.color === 'purple' ? 'bg-purple-100 text-purple-600' :
                metric.color === 'orange' ? 'bg-orange-100 text-orange-600' :
                'bg-gray-100 text-gray-600'
              }`}>
                <metric.icon className="h-4 w-4" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Benchmarks */}
      {benchmarks && (
        <Card variant="elevated" padding="md">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Industry Benchmarks</h4>
          {benchmarks.totalBenchmarks > 0 ? (
            <div className="space-y-3">
              {Object.entries(benchmarks.benchmarks).map(([metric, benchmarkList]) => (
                <div key={metric} className="border-b border-gray-100 pb-3 last:border-b-0">
                  <h5 className="font-medium text-gray-900 mb-2 text-sm">{metric}</h5>
                  <div className="grid grid-cols-1 gap-2">
                    {benchmarkList.map((benchmark, index) => (
                      <div key={index} className="text-center p-2 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-600">{benchmark.benchmarkType}</p>
                        <p className="text-base font-semibold text-gray-900">
                          {benchmark.benchmarkValue}
                        </p>
                        <p className="text-xs text-gray-500">
                          {benchmark.percentile}th percentile
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4 text-sm">No benchmark data available</p>
          )}
        </Card>
      )}
    </div>
  );
};

// Insights Tab Component
interface InsightsTabProps {
  insights: NewsletterInsights | null;
  isLoading: boolean;
}

const InsightsTab: React.FC<InsightsTabProps> = ({ insights, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!insights || insights.insights.all.length === 0) {
    return (
      <div className="text-center py-8">
        <Brain className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">No insights available for this post</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <Card variant="elevated" padding="md">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Insights Summary</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <p className="text-xl font-bold text-gray-900">{insights.summary.totalInsights}</p>
            <p className="text-xs text-gray-500">Total</p>
          </div>
          <div className="text-center p-2 bg-red-50 rounded-lg">
            <p className="text-xl font-bold text-red-600">
              {insights.summary.priorityBreakdown.high || 0}
            </p>
            <p className="text-xs text-gray-500">High Priority</p>
          </div>
          <div className="text-center p-2 bg-yellow-50 rounded-lg">
            <p className="text-xl font-bold text-yellow-600">
              {insights.summary.priorityBreakdown.medium || 0}
            </p>
            <p className="text-xs text-gray-500">Medium</p>
          </div>
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <p className="text-xl font-bold text-gray-900">
              {(() => {
                const score = insights.summary.averageConfidenceScore;
                if (score == null) return 'N/A';
                return `${(score * 100).toFixed(1)}%`;
              })()}
            </p>
            <p className="text-xs text-gray-500">Avg Confidence</p>
          </div>
        </div>
      </Card>

      {/* Insights by Priority */}
      <div className="space-y-3">
        {['high', 'medium', 'low'].map(priority => {
          const priorityInsights = insights.insights.all.filter(
            insight => insight.insightsPriority === priority
          );

          if (priorityInsights.length === 0) return null;

          return (
            <Card key={priority} variant="outlined" padding="md">
              <h5 className={`font-semibold mb-2 text-sm ${
                priority === 'high' ? 'text-red-600' :
                priority === 'medium' ? 'text-yellow-600' :
                'text-gray-600'
              }`}>
                {priority.charAt(0).toUpperCase() + priority.slice(1)} Priority
              </h5>
              <div className="space-y-2">
                {priorityInsights.map((insight, index) => (
                  <div key={index} className="p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-start justify-between mb-1">
                      <h6 className="font-medium text-gray-900 text-sm">{insight.title}</h6>
                      <span className="text-xs text-gray-500">
                        {(() => {
                          const score = typeof insight.confidenceScore === 'number'
                            ? insight.confidenceScore
                            : parseFloat(String(insight.confidenceScore));
                          return `${(score * 100).toFixed(0)}%`;
                        })()}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">{insight.insightsDescription}</p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{insight.insightCategory}</span>
                      <span>{insight.insightType}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// Content Tab Component
interface ContentTabProps {
  post: NewsletterPost;
}

const ContentTab: React.FC<ContentTabProps> = ({ post }) => {
  return (
    <div className="space-y-4">
      <Card variant="elevated" padding="md">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Content Overview</h4>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-600">Title</label>
            <p className="text-gray-900 mt-1 text-sm">{post.title}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Publish Date</label>
            <p className="text-gray-900 mt-1 text-sm">{new Date(post.publishDate).toLocaleDateString()}</p>
          </div>
        </div>
      </Card>

      <Card variant="outlined" padding="md">
        <h5 className="font-semibold text-gray-900 mb-2 text-sm">Content Analysis</h5>
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">
            Detailed content analysis will be available when integrated with content analysis API endpoints.
          </p>
        </div>
      </Card>
    </div>
  );
};

// Click Analysis Tab Component
interface ClickAnalysisTabProps {
  clicks: NewsletterPostClicks | null;
}

const ClickAnalysisTab: React.FC<ClickAnalysisTabProps> = ({ clicks }) => {
  const formatPercentage = (value: string) => `${parseFloat(value).toFixed(1)}%`;
  const formatNumber = (value: number) => value.toLocaleString();

  if (!clicks) {
    return (
      <div className="text-center py-8">
        <MousePointer className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-sm font-medium text-gray-900 mb-2">No Click Data Available</h3>
        <p className="text-gray-500 text-xs">Click analytics will appear here when data is available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <Card variant="elevated" padding="md">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Click Summary</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="text-center p-2 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{formatNumber(clicks.summary.totalClicks)}</p>
            <p className="text-xs text-gray-600 font-medium">Total Clicks</p>
          </div>
          <div className="text-center p-2 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">{formatNumber(clicks.summary.totalUniqueClicks)}</p>
            <p className="text-xs text-gray-600 font-medium">Unique Clicks</p>
          </div>
          <div className="text-center p-2 bg-purple-50 rounded-lg">
            <p className="text-2xl font-bold text-purple-600">{formatNumber(clicks.summary.totalUrls)}</p>
            <p className="text-xs text-gray-600 font-medium">Total URLs</p>
          </div>
          <div className="text-center p-2 bg-orange-50 rounded-lg">
            <p className="text-2xl font-bold text-orange-600">{clicks.summary.topDomains.length}</p>
            <p className="text-xs text-gray-600 font-medium">Domains</p>
          </div>
        </div>
      </Card>

      {/* Channel Breakdown */}
      <Card variant="elevated" padding="md">
        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
          <Eye className="h-4 w-4 mr-2 text-blue-600" />
          Email vs Web Clicks
        </h4>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
            <span className="font-medium text-gray-900 text-sm">Email Clicks</span>
            <span className="text-lg font-bold text-blue-600">
              {formatNumber(clicks.summary.totalEmailClicks)}
            </span>
          </div>
          <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
            <span className="font-medium text-gray-900 text-sm">Web Clicks</span>
            <span className="text-lg font-bold text-green-600">
              {formatNumber(clicks.summary.totalWebClicks)}
            </span>
          </div>
        </div>
      </Card>

      {/* Top Performing URLs */}
      <Card variant="elevated" padding="md">
        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
          <Link className="h-4 w-4 mr-2 text-green-600" />
          Top URLs
        </h4>
        <div className="space-y-2">
          {clicks.clickData.mostRecentByUrl.slice(0, 5).map((clickData, index) => (
            <div key={index} className="flex items-center justify-between p-2 border border-gray-200 rounded-lg">
              <div className="flex-1 min-w-0 mr-2">
                <p className="text-xs font-medium text-gray-900 truncate">
                  {clickData.postClicksUrl}
                </p>
                <p className="text-xs text-gray-500">{clickData.clickDomain}</p>
              </div>
              <div className="text-right">
                <span className="text-sm font-bold text-gray-900">
                  {formatNumber(clickData.totalClicks)}
                </span>
                <p className="text-xs text-gray-500">
                  {formatPercentage(clickData.totalClickThroughRate)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Top Domains */}
      <Card variant="elevated" padding="md">
        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
          <Globe className="h-4 w-4 mr-2 text-indigo-600" />
          Top Domains
        </h4>
        <div className="space-y-2">
          {clicks.summary.topDomains.slice(0, 5).map((domain, index) => {
            const domainClicks = clicks.clickData.byDomain[domain] || [];
            const totalDomainClicks = domainClicks.reduce((sum, click) => sum + click.totalClicks, 0);

            return (
              <div key={index} className="flex items-center justify-between p-2 border border-gray-200 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900 text-sm">{domain}</p>
                  <p className="text-xs text-gray-500">{domainClicks.length} URLs</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900">{formatNumber(totalDomainClicks)}</p>
                  <p className="text-xs text-gray-500">clicks</p>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* UTM Sources */}
      {clicks.summary.topUtmSources.length > 0 && (
        <Card variant="elevated" padding="md">
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
            <Hash className="h-4 w-4 mr-2 text-yellow-600" />
            UTM Sources
          </h4>
          <div className="space-y-2">
            {clicks.summary.topUtmSources.slice(0, 5).map((source, index) => {
              const sourceClicks = clicks.clickData.byUtmSource[source] || [];
              const totalSourceClicks = sourceClicks.reduce((sum, click) => sum + click.totalClicks, 0);

              return (
                <div key={index} className="flex items-center justify-between p-2 border border-gray-200 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{source}</p>
                    <p className="text-xs text-gray-500">{sourceClicks.length} URLs</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">{formatNumber(totalSourceClicks)}</p>
                    <p className="text-xs text-gray-500">clicks</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};

// Analysis Tab Component
interface AnalysisTabProps {
  postId: string;
}

const AnalysisTab: React.FC<AnalysisTabProps> = ({ postId }) => {
  const [analysis, setAnalysis] = useState<ContentAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAnalysis = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await newsletterService.getPostContentAnalysis(postId);

        if (response.success && response.data?.latestAnalysis) {
          setAnalysis(response.data.latestAnalysis);
        } else {
          setAnalysis(null);
        }
      } catch (err) {
        console.error('Failed to load content analysis:', err);
        setError('Failed to load content analysis');
        setAnalysis(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadAnalysis();
  }, [postId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="text-center py-8">
        <Sparkles className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500 text-sm">
          {error || 'No content analysis available for this post'}
        </p>
      </div>
    );
  }

  const getSentimentColor = (score: number) => {
    if (score >= 0.6) return 'text-green-600';
    if (score >= 0.4) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSentimentLabel = (score: number) => {
    if (score >= 0.6) return 'Positive';
    if (score >= 0.4) return 'Neutral';
    return 'Negative';
  };

  const getReadabilityLabel = (score: number) => {
    if (score >= 80) return 'Very Easy';
    if (score >= 60) return 'Easy';
    if (score >= 40) return 'Moderate';
    if (score >= 20) return 'Difficult';
    return 'Very Difficult';
  };

  return (
    <div className="space-y-4">
      {/* Content Metrics */}
      <Card variant="elevated" padding="md">
        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
          <BookOpen className="h-4 w-4 mr-2 text-blue-600" />
          Content Metrics
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-2xl font-bold text-blue-900">{analysis.wordCount.toLocaleString()}</p>
            <p className="text-xs text-blue-700 font-medium">Word Count</p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-2xl font-bold text-purple-900">{analysis.subjectLineLength}</p>
            <p className="text-xs text-purple-700 font-medium">Subject Length</p>
          </div>
        </div>
      </Card>

      {/* Sentiment & Readability */}
      <Card variant="elevated" padding="md">
        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
          <Smile className="h-4 w-4 mr-2 text-yellow-600" />
          Sentiment & Readability
        </h4>
        <div className="space-y-3">
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-600">Sentiment Score</span>
              <span className={`text-sm font-bold ${getSentimentColor(analysis.sentimentScore)}`}>
                {getSentimentLabel(analysis.sentimentScore)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  analysis.sentimentScore >= 0.6 ? 'bg-green-500' :
                  analysis.sentimentScore >= 0.4 ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}
                style={{ width: `${analysis.sentimentScore * 100}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">{(analysis.sentimentScore * 100).toFixed(1)}%</p>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-600">Readability Score</span>
              <span className="text-sm font-bold text-indigo-900">
                {getReadabilityLabel(analysis.readabilityScore)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-indigo-500 h-2 rounded-full"
                style={{ width: `${analysis.readabilityScore}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">{analysis.readabilityScore.toFixed(1)}/100</p>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-xs font-medium text-gray-600">Contains Emoji</span>
            <span className={`text-sm font-bold ${analysis.hasEmoji ? 'text-green-600' : 'text-gray-500'}`}>
              {analysis.hasEmoji ? '✓ Yes' : '✗ No'}
            </span>
          </div>
        </div>
      </Card>

      {/* Topics */}
      {analysis.primaryTopics && analysis.primaryTopics.length > 0 && (
        <Card variant="elevated" padding="md">
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
            <FileText className="h-4 w-4 mr-2 text-indigo-600" />
            Primary Topics
          </h4>
          <div className="flex flex-wrap gap-2">
            {analysis.primaryTopics.map((topic, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium"
              >
                {topic}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Keywords */}
      {analysis.keywords && analysis.keywords.length > 0 && (
        <Card variant="elevated" padding="md">
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
            <Tag className="h-4 w-4 mr-2 text-green-600" />
            Keywords
          </h4>
          <div className="flex flex-wrap gap-2">
            {analysis.keywords.map((keyword, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium"
              >
                {keyword}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Entities */}
      {analysis.entities && analysis.entities.length > 0 && (
        <Card variant="elevated" padding="md">
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
            <Sparkles className="h-4 w-4 mr-2 text-purple-600" />
            Entities
          </h4>
          <div className="flex flex-wrap gap-2">
            {analysis.entities.map((entity, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium"
              >
                {entity}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Analysis Date */}
      <div className="text-center text-xs text-gray-500">
        Last analyzed: {new Date(analysis.analysisDate).toLocaleDateString()}
      </div>
    </div>
  );
};


export default PostDetailDrawer;
